/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Abonnement.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - PAGE GESTION ABONNEMENTS

📋 DESCRIPTION:
Page React pour la gestion des abonnements Standard et Premium.
Interface commerciale avec comparaison plans et demande d'upgrade.

🎯 RESPONSABILITÉS:
- Affichage comparatif des plans Standard vs Premium
- Gestion du type d'abonnement actuel utilisateur
- Formulaire de demande d'upgrade vers Premium
- Interface de démonstration pour changement de plan
- Présentation des fonctionnalités et avantages par plan
- Modal de contact avec validation et envoi de demande
- Section informative sur l'optimisation calepinage

🔗 DÉPENDANCES:
- SubscriptionService: Gestion type abonnement et demandes contact
- React Router: Navigation et gestion état
- React: Hooks useState, useEffect pour gestion formulaires

📡 INTERACTIONS:
- Service: Récupération/modification type abonnement actuel
- Formulaire: Validation et envoi demandes contact Premium
- Interface: Comparaison visuelle plans avec mise en évidence
- Modal: Formulaire contact avec gestion états loading/success
- Démonstration: Changement type abonnement pour tests

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface responsive avec grille adaptative
- Mise en évidence plan recommandé (Premium)
- Formulaire modal avec validation complète
- Section démonstration pour tests administrateur
- Gestion états loading/success pour UX optimale
- Présentation claire ROI optimisation calepinage
- Design commercial avec call-to-action efficaces
*/
import React, { useState, useContext } from 'react';
import { subscriptionService, SubscriptionType } from '../services/SubscriptionService';
import AuthContext from '../contexts/AuthContext';
import { apiFetch } from '../services/httpClient';

const Abonnement: React.FC = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const subscriptionType: SubscriptionType = user?.subscriptionType ?? 'standard';
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [success, setSuccess] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);

  // Gérer les changements dans le formulaire
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Soumettre la demande de contact
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await subscriptionService.requestPremiumContact(
        formData.name,
        formData.email,
        formData.phone
      );
      
      if (result) {
        setSuccess(true);
        setShowContactForm(false);
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de la demande:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminChange = async (type: SubscriptionType) => {
    if (!user) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      await apiFetch(`/admin/users/${user.id}/subscription`, {
        method: 'PUT',
        body: JSON.stringify({ subscription_type: type }),
      });

      await refreshUser();
    } catch (error) {
      if (error instanceof Error) {
        setAdminError(error.message);
      } else {
        setAdminError('Erreur lors de la mise a jour de labonnement');
      }
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-2xl font-semibold mb-6">Abonnements</h1>
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          <p className="font-medium">Demande envoyée avec succès !</p>
          <p>Notre équipe vous contactera prochainement pour discuter de votre abonnement Premium.</p>
        </div>
      )}

      {adminError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {adminError}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Plan Standard */}
        <div className={`border rounded-lg p-6 ${subscriptionType === 'standard' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
          <h2 className="text-xl font-medium mb-4">Standard</h2>
          <p className="text-gray-600 mb-4">
            Accédez aux fonctionnalités essentielles pour gérer vos devis et articles.
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Configuration d'articles
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Calcul de prix détaillé
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Gestion des devis
            </li>
            <li className="flex items-center text-gray-400">
              <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Optimisation du calepinage
            </li>
            <li className="flex items-center text-gray-400">
              <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Visualisation du placement des pièces
            </li>
          </ul>
          <div className="text-center">
            <div className="text-2xl font-bold mb-1">
              <span className="line-through text-gray-400 mr-2">29,99 € TTC</span>
              19 € TTC <span className="text-sm font-normal">/mois</span>
            </div>
            <div className="text-xs font-medium text-indigo-700 mb-2">Offre de lancement</div>
            <div className="text-sm text-gray-600 mb-4">
              Mensuel • Engagement initial 3 mois • Résiliation après engagement à la prochaine échéance de facturation
            </div>
            {subscriptionType === 'standard' ? (
              <button
                type="button"
                className="w-full py-2 px-4 bg-gray-200 text-gray-600 rounded-md cursor-not-allowed"
                disabled
              >
                Abonnement actuel
              </button>
            ) : (
              user?.role === 'admin' ? (
                <button
                  type="button"
                  className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-60"
                  onClick={() => handleAdminChange('standard')}
                  disabled={adminLoading}
                >
                  Basculer en Standard (admin)
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full py-2 px-4 bg-gray-200 text-gray-600 rounded-md cursor-not-allowed"
                  disabled
                >
                  Contactez un administrateur
                </button>
              )
            )}
          </div>
        </div>
        
        {/* Plan Premium */}
        <div className={`border rounded-lg p-6 relative ${subscriptionType === 'premium' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
          <div className="absolute top-0 right-0 bg-indigo-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-medium">
            Recommandé
          </div>
          <h2 className="text-xl font-medium mb-4">Premium</h2>
          <p className="text-gray-600 mb-4">
            Optimisez vos coûts et améliorez votre rentabilité grâce aux fonctionnalités avancées.
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Configuration d'articles
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Calcul de prix détaillé
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Gestion des devis
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <strong>Optimisation du calepinage</strong>
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <strong>Visualisation du placement des pièces</strong>
            </li>
            <li className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <strong>Économies jusqu'à 15% sur les matières</strong>
            </li>
          </ul>
          <div className="text-center">
            <div className="text-2xl font-bold mb-1">
              <span className="line-through text-gray-400 mr-2">49,99 € TTC</span>
              29 € TTC <span className="text-sm font-normal">/mois</span>
            </div>
            <div className="text-xs font-medium text-indigo-700 mb-2">Offre de lancement</div>
            <div className="text-sm text-gray-600 mb-4">
              Mensuel • Engagement initial 3 mois • Résiliation après engagement à la prochaine échéance de facturation
            </div>
            {subscriptionType === 'premium' ? (
              <button
                type="button"
                className="w-full py-2 px-4 bg-gray-200 text-gray-600 rounded-md cursor-not-allowed"
                disabled
              >
                Abonnement actuel
              </button>
            ) : (
              user?.role === 'admin' ? (
                <button
                  type="button"
                  className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                  onClick={() => handleAdminChange('premium')}
                  disabled={adminLoading}
                >
                  Définir Premium (admin)
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  onClick={() => setShowContactForm(true)}
                >
                  Demander Premium
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Tableau comparatif */}
      <div className="mt-10 bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Comparatif des offres</h2>
          <p className="text-sm text-gray-600 mt-1">
            Visualisez en un coup d'œil les fonctionnalités incluses dans Standard et Premium.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonctionnalité</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Standard</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Premium</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                { label: "Configuration d'articles & calcul détaillé", standard: true, premium: true },
                { label: "Gestion des devis (PDF, clients, remises)", standard: true, premium: true },
                { label: "Optimisation du calepinage (économies matières)", standard: false, premium: true },
                { label: "Visualisation du placement des pièces", standard: false, premium: true },
                { label: "Intégration des économies dans le prix client", standard: false, premium: true },
                { label: "Support prioritaire & accompagnement", standard: false, premium: true },
              ].map((row, idx) => (
                <tr key={idx}>
                  <td className="px-6 py-4 text-sm text-gray-800">{row.label}</td>
                  <td className="px-6 py-4 text-center">
                    {row.standard ? (
                      <span className="inline-flex items-center justify-center rounded-full bg-green-50 text-green-700 w-8 h-8 mx-auto">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-400 w-8 h-8 mx-auto">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {row.premium ? (
                      <span className="inline-flex items-center justify-center rounded-full bg-indigo-50 text-indigo-700 w-8 h-8 mx-auto">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center rounded-full bg-gray-50 text-gray-400 w-8 h-8 mx-auto">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Formulaire de demande de contact */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium">Demande d'abonnement Premium</h2>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowContactForm(false)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message (optionnel)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowContactForm(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Section d'information */}
      <div className="mt-12 bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-medium mb-4">Comment fonctionne l'abonnement Premium ?</h2>
        <p className="mb-4">
          L'abonnement Premium vous donne accès à des fonctionnalités avancées qui vous permettent d'optimiser vos coûts de production et d'améliorer votre rentabilité.
        </p>
        <p className="mb-4">
          La fonctionnalité d'optimisation du calepinage analyse intelligemment le placement des pièces sur les matières premières pour minimiser les chutes et réduire les coûts. Vous pouvez visualiser le placement optimal des pièces et voir les économies réalisées en temps réel.
        </p>
        <p>
          Pour obtenir un abonnement Premium, cliquez sur "Demander Premium" et remplissez le formulaire. Notre équipe vous contactera rapidement pour discuter de vos besoins et vous accompagner dans la mise en place de votre abonnement.
        </p>
      </div>
      
      {user?.role === 'admin' && (
        <div className="mt-8 border-t pt-8">
          <h3 className="text-lg font-medium mb-4">Bandeau administrateur</h3>
          <p className="text-sm text-gray-500 mb-4">
            Les changements Standard/Premium sont appliqués en base de données et propagés aux utilisateurs via /auth/verify.
          </p>
          <div className="flex space-x-4">
            <button
              type="button"
              className={`px-4 py-2 rounded-md ${subscriptionType === 'standard' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'} disabled:opacity-60`}
              onClick={() => handleAdminChange('standard')}
              disabled={adminLoading}
            >
              Définir Standard
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-md ${subscriptionType === 'premium' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'} disabled:opacity-60`}
              onClick={() => handleAdminChange('premium')}
              disabled={adminLoading}
            >
              Définir Premium
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Abonnement;
